import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {Link, useParams} from "react-router-dom";
import {createChapter, deleteChapter, getAdminCourseDetails, updateChapter} from "../api/api";
import {Chapter, CourseDetailsResponse} from "../types";
import {
    Box,
    Breadcrumbs,
    Button,
    CircularProgress,
    Container,
    IconButton,
    List,
    Modal,
    Pagination,
    Paper,
    TextField,
    Typography
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import {useAlert} from "../context/AlertContext";
import AppModalBox from "../components/AppModalBox";
import AddIcon from "@mui/icons-material/Add";
import BreadcrumbLink from "../components/BreadcrumbLink";

const CourseDetailsPage = () => {
    const {t} = useTranslation();

    const {courseId} = useParams<{ courseId?: string }>();
    const safeCourseId = courseId ?? "";
    const {showAlert} = useAlert();

    const [data, setData] = useState<CourseDetailsResponse | null>(null);

    const [chapters, setChapters] = useState<Chapter[]>([]);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);

    const [newChapterTitle, setNewChapterTitle] = useState("");
    const [newChapterOrder, setNewChapterOrder] = useState<number>(1);
    const [openCreateModal, setOpenCreateModal] = useState(false);

    const [editingChapter, setEditingChapter] = useState<Chapter | null>(null);
    const [openEditModal, setOpenEditModal] = useState(false);

    const [chapterToDelete, setChapterToDelete] = useState<Chapter | null>(null);

    const pageSize = 10;

    const [loading, setLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    const fetchData = async (uiPage: number) => {
        if (!safeCourseId) return;

        setLoading(true);
        setHasError(false);

        try {
            const res = await getAdminCourseDetails(Number(safeCourseId), uiPage - 1, pageSize);

            setData(res);

            const pageInfo = res.chapters?.page;
            setChapters(res.chapters?.content ?? []);
            setTotalPages(pageInfo?.totalPages ?? 1);
            setCurrentPage((pageInfo?.number ?? 0) + 1);
        } catch (e: any) {
            setHasError(true);

            showAlert(e?.message ?? t("coursedetails.errors.loadFailed"), "error");
            setData(null);
            setChapters([]);
            setTotalPages(1);
            setCurrentPage(uiPage);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            if (!safeCourseId) return;

            setLoading(true);
            setHasError(false);

            try {
                const res = await getAdminCourseDetails(Number(safeCourseId), 0, pageSize);
                if (cancelled) return;

                setData(res);

                const pageInfo = res.chapters?.page;
                setChapters(res.chapters?.content ?? []);
                setTotalPages(pageInfo?.totalPages ?? 1);
                setCurrentPage((pageInfo?.number ?? 0) + 1);
            } catch (e: any) {
                if (cancelled) return;

                setHasError(true);
                showAlert(e?.message ?? t("coursedetails.errors.loadFailed"), "error");
                setData(null);
                setChapters([]);
                setTotalPages(1);
                setCurrentPage(1);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        run();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [safeCourseId]);

    const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
        fetchData(value);
    };

    const handleOpenCreateModal = () => {
        setNewChapterOrder(chapters.length + 1);
        setOpenCreateModal(true);
    };

    const handleAddChapter = () => {
        if (!newChapterTitle.trim() || !safeCourseId) return;

        createChapter({title: newChapterTitle, orderNum: newChapterOrder, courseId: safeCourseId})
            .then(() => {
                showAlert(t("coursedetails.alerts.added"), "success");
                setNewChapterTitle("");
                setNewChapterOrder(chapters.length + 1);
                setOpenCreateModal(false);
                fetchData(currentPage);
            })
            .catch((e: any) => showAlert(e?.message ?? t("coursedetails.errors.addFailed"), "error"));
    };

    const handleDeleteChapter = () => {
        if (!chapterToDelete) return;

        deleteChapter(chapterToDelete.id.toString())
            .then(() => {
                showAlert(
                    t("coursedetails.alerts.deleted", {title: chapterToDelete.title}),
                    "success"
                );
                setChapterToDelete(null);
                const willBeEmpty = chapters.length === 1 && currentPage > 1;
                fetchData(willBeEmpty ? currentPage - 1 : currentPage);
            })
            .catch((e: any) => showAlert(e?.message ?? t("coursedetails.errors.deleteFailed"), "error"));
    };

    const handleEditChapter = (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingChapter) return;

        updateChapter(editingChapter.id.toString(), {
            title: editingChapter.title,
            orderNum: editingChapter.orderNum,
            courseId: safeCourseId
        })
            .then(() => {
                showAlert(t("coursedetails.alerts.updated"), "success");
                setOpenEditModal(false);
                setEditingChapter(null);
                fetchData(currentPage);
            })
            .catch((e: any) => showAlert(e?.message ?? t("coursedetails.errors.updateFailed"), "error"));
    };

    if (loading && !data) {
        return (
            <Container sx={{mt: 6, display: "flex", justifyContent: "center"}}>
                <CircularProgress/>
            </Container>
        );
    }

    if (hasError) {
        return (
            <Container sx={{mt: 6, display: "flex", flexDirection: "column", alignItems: "center", gap: 2}}>
                <Typography color="text.secondary">
                    {t("coursedetails.errors.loadFailed")}
                </Typography>

                <Box sx={{display: "flex", gap: 2}}>
                    <Button variant="contained" onClick={() => fetchData(currentPage || 1)}>
                        {t("common.retry")}
                    </Button>

                    <Button component={Link} to="/" variant="outlined">
                        {t("common.toMainPage")}
                    </Button>
                </Box>
            </Container>
        );
    }

    if (!data) return null;

    return (
        <Box sx={{px: {xs: 3, md: 3}}}>
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small"/>} sx={{mb: 2}}>
                <BreadcrumbLink to="/">
                    {t("nav.courses")}
                </BreadcrumbLink>

                <Typography variant="body2" color="text.primary">
                    <strong>{data?.title || t("coursedetails.courseDetails")}</strong>
                </Typography>
            </Breadcrumbs>

            <Box
                sx={{
                    mb: 3,
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 2,
                }}
            >
                <Box sx={{minWidth: 0}}>
                    <Typography sx={{fontSize: 28, fontWeight: 800}} noWrap>
                        {data.title}
                    </Typography>

                    <Typography variant="body2" sx={{color: "text.secondary", mt: 0.5}}>
                        {data.description}
                    </Typography>
                </Box>

                <IconButton
                    onClick={handleOpenCreateModal}
                    aria-label={t("coursedetails.addChapter")}
                    sx={(theme) => ({
                        width: 44,
                        height: 44,
                        borderRadius: "50%",
                        backgroundColor: theme.palette.action.hover,
                        color: theme.palette.text.primary,
                        flexShrink: 0,
                        "&:hover": {
                            backgroundColor: theme.palette.action.selected,
                        },
                    })}
                >
                    <AddIcon/>
                </IconButton>
            </Box>

            {loading && data && (
                <Box sx={{display: "flex", justifyContent: "center", mb: 2}}>
                    <CircularProgress size={24}/>
                </Box>
            )}

            <List sx={{display: "flex", flexDirection: "column", gap: 3}}>
                {chapters.map((chapter) => (
                    <Paper
                        key={chapter.id}
                        elevation={0}
                        sx={(theme) => ({
                            px: 3,
                            py: 2,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                            "&:hover": {backgroundColor: theme.palette.action.hover},
                        })}
                    >
                        <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2}}>
                            <Box sx={{minWidth: 0}}>
                                <Typography sx={{fontWeight: 700}} noWrap>
                                    {chapter.title}
                                </Typography>

                                <Typography variant="body2" sx={{color: "text.secondary"}} noWrap>
                                    {t("coursedetails.order")}: {chapter.orderNum}
                                </Typography>
                            </Box>

                            <Box sx={{display: "flex", alignItems: "center", gap: 2}}>
                                <Button component={Link} to={`/chapter/${chapter.id}`} variant="outlined" size="small">
                                    {t("coursedetails.details")}
                                </Button>

                                <IconButton
                                    onClick={() => {
                                        setEditingChapter(chapter);
                                        setOpenEditModal(true);
                                    }}
                                    size="small"
                                    aria-label={t("common.edit")}
                                >
                                    <EditIcon fontSize="small"/>
                                </IconButton>

                                <IconButton
                                    onClick={() => setChapterToDelete(chapter)}
                                    size="small"
                                    aria-label={t("common.delete")}
                                >
                                    <DeleteIcon fontSize="small"/>
                                </IconButton>
                            </Box>
                        </Box>
                    </Paper>
                ))}
            </List>

            {chapters.length === 0 && !loading && (
                <Typography sx={{mt: 3}} color="text.secondary">
                    {t("coursedetails.noChapters")}
                </Typography>
            )}

            <Box sx={{display: "flex", justifyContent: "center", mt: 3}}>
                <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                />
            </Box>

            <DeleteConfirmationModal
                open={chapterToDelete !== null}
                onClose={() => setChapterToDelete(null)}
                onConfirm={handleDeleteChapter}
            />

            <Modal open={openCreateModal} onClose={() => setOpenCreateModal(false)}>
                <AppModalBox>
                    <Typography variant="h6" sx={{mb: 2}}>
                        {t("coursedetails.addChapter")}
                    </Typography>

                    <TextField
                        label={t("coursedetails.chapterTitle")}
                        fullWidth
                        required
                        value={newChapterTitle}
                        onChange={(e) => setNewChapterTitle(e.target.value)}
                        sx={{mb: 2}}
                    />

                    <TextField
                        label={t("coursedetails.orderNumber")}
                        type="number"
                        fullWidth
                        required
                        value={newChapterOrder}
                        onChange={(e) => setNewChapterOrder(Number(e.target.value))}
                        sx={{mb: 2}}
                    />

                    <Button variant="contained" onClick={handleAddChapter}>
                        {t("common.create")}
                    </Button>
                </AppModalBox>
            </Modal>

            <Modal
                open={openEditModal}
                onClose={() => {
                    setOpenEditModal(false);
                    setEditingChapter(null);
                }}
            >
                <AppModalBox>
                    <Typography variant="h6" sx={{mb: 2}}>
                        {t("coursedetails.editChapter")}
                    </Typography>

                    <form onSubmit={handleEditChapter}>
                        <TextField
                            label={t("coursedetails.chapterTitle")}
                            fullWidth
                            required
                            value={editingChapter?.title || ""}
                            onChange={(e) =>
                                setEditingChapter((prev) => (prev ? {...prev, title: e.target.value} : null))
                            }
                            sx={{mb: 2}}
                        />

                        <TextField
                            label={t("coursedetails.order")}
                            type="number"
                            fullWidth
                            required
                            value={editingChapter?.orderNum ?? ""}
                            onChange={(e) =>
                                setEditingChapter((prev) =>
                                    prev ? {...prev, orderNum: Number(e.target.value)} : null
                                )
                            }
                            sx={{mb: 2}}
                        />

                        <Button type="submit" variant="contained" sx={{mt: 2}}>
                            {t("coursedetails.saveChanges")}
                        </Button>
                    </form>
                </AppModalBox>
            </Modal>
        </Box>
    );
};

export default CourseDetailsPage;